import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { toStatePlane, detectZone } from '../../utils/coordinateUtils';
import { metersToFeet, calculatePolygonAreaSqFt, calculatePerimeterMeters } from '../../utils/measurementUtils';

// Tooltip CSS class (injected once)
let tooltipStyleInjected = false;
function injectTooltipStyle() {
  if (tooltipStyleInjected) return;
  const style = document.createElement('style');
  style.textContent = `
    .measurement-tooltip {
      background-color: rgba(0, 0, 0, 0.75) !important;
      color: white !important;
      border: none !important;
      border-radius: 4px !important;
      padding: 4px 8px !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      white-space: nowrap !important;
      box-shadow: 0 1px 4px rgba(0,0,0,0.3) !important;
    }
    .measurement-tooltip::before {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
  tooltipStyleInjected = true;
}

function MeasurementController({ activeTool, measurementData, setMeasurementData, gpsMarkers }) {
  const map = useMap();
  const distanceLayerGroup = useRef(L.layerGroup());
  const areaLayerGroup = useRef(L.layerGroup());
  const rubberBandRef = useRef(null);
  const isMobileRef = useRef('ontouchstart' in window);
  const clickTimerRef = useRef(null);
  const finishedRef = useRef(false);

  // Inject tooltip style on mount
  useEffect(() => {
    injectTooltipStyle();
  }, []);

  // Add/remove layer groups from map
  useEffect(() => {
    distanceLayerGroup.current.addTo(map);
    areaLayerGroup.current.addTo(map);
    return () => {
      distanceLayerGroup.current.remove();
      areaLayerGroup.current.remove();
    };
  }, [map]);

  // Set crosshair cursor and disable overlay pointer events when tool active
  useEffect(() => {
    let styleEl = null;
    if (activeTool && activeTool !== 'gps') {
      map.getContainer().style.cursor = 'crosshair';
      // Inject CSS to force pointer-events off on all overlay paths
      // Leaflet sets pointer-events on individual <path> elements,
      // so we need !important to override
      styleEl = document.createElement('style');
      styleEl.setAttribute('data-measurement-override', 'true');
      styleEl.textContent = `
        .leaflet-overlay-pane path,
        .leaflet-overlay-pane svg {
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(styleEl);
    } else {
      map.getContainer().style.cursor = '';
    }
    return () => {
      map.getContainer().style.cursor = '';
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, [activeTool, map]);

  // Reset finished flag when tool changes or measurement is cleared
  useEffect(() => {
    if (measurementData.distancePoints.length === 0 && measurementData.areaPoints.length === 0) {
      finishedRef.current = false;
    }
  }, [measurementData.distancePoints.length, measurementData.areaPoints.length]);

  // Cleanup layers when tool changes
  useEffect(() => {
    if (activeTool !== 'distance') {
      distanceLayerGroup.current.clearLayers();
    }
    if (activeTool !== 'area') {
      areaLayerGroup.current.clearLayers();
    }
    if (rubberBandRef.current) {
      rubberBandRef.current.remove();
      rubberBandRef.current = null;
    }
    // Clear any pending click timer
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    finishedRef.current = false;
  }, [activeTool]);

  // ========================
  // DISTANCE TOOL
  // ========================
  useEffect(() => {
    if (activeTool !== 'distance') return;

    map.doubleClickZoom.disable();
    const points = measurementData.distancePoints;

    const drawDistanceLayers = () => {
      distanceLayerGroup.current.clearLayers();

      for (let i = 0; i < points.length; i++) {
        L.circleMarker([points[i].lat, points[i].lng], {
          radius: 6, color: '#ef4444', fillColor: '#ffffff', fillOpacity: 1, weight: 2
        }).addTo(distanceLayerGroup.current);

        if (i > 0) {
          L.polyline(
            [[points[i - 1].lat, points[i - 1].lng], [points[i].lat, points[i].lng]],
            { color: '#ef4444', weight: 3, dashArray: '10, 8' }
          ).addTo(distanceLayerGroup.current);

          const midLat = (points[i - 1].lat + points[i].lat) / 2;
          const midLng = (points[i - 1].lng + points[i].lng) / 2;
          const segDist = map.distance(
            L.latLng(points[i - 1].lat, points[i - 1].lng),
            L.latLng(points[i].lat, points[i].lng)
          );
          const feet = metersToFeet(segDist);
          const label = feet < 1000 ? `${feet.toFixed(1)} ft` : `${feet.toLocaleString('en-US', { maximumFractionDigits: 1 })} ft`;

          L.tooltip({ permanent: true, direction: 'center', className: 'measurement-tooltip' })
            .setLatLng([midLat, midLng])
            .setContent(label)
            .addTo(distanceLayerGroup.current);
        }
      }
    };

    const addPoint = (latlng) => {
      if (finishedRef.current) return;

      const newPoint = { lat: latlng.lat, lng: latlng.lng };
      const newPoints = [...points, newPoint];

      const segments = [];
      let total = 0;
      for (let i = 1; i < newPoints.length; i++) {
        const d = map.distance(
          L.latLng(newPoints[i - 1].lat, newPoints[i - 1].lng),
          L.latLng(newPoints[i].lat, newPoints[i].lng)
        );
        segments.push(d);
        total += d;
      }

      setMeasurementData(prev => ({
        ...prev,
        distancePoints: newPoints,
        distanceSegments: segments,
        distanceTotal: total
      }));
    };

    const onClick = (e) => {
      if (finishedRef.current) return;
      // Delay click to allow dblclick to cancel it
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => {
        addPoint(e.latlng);
        clickTimerRef.current = null;
      }, 250);
    };

    const onDblClick = (e) => {
      // Cancel the pending click so we don't add an extra point
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      L.DomEvent.stopPropagation(e);
      L.DomEvent.preventDefault(e);
      finishedRef.current = true;
      // Remove rubber band
      if (rubberBandRef.current) {
        rubberBandRef.current.remove();
        rubberBandRef.current = null;
      }
    };

    const onMouseMove = (e) => {
      if (isMobileRef.current || points.length === 0 || finishedRef.current) return;
      if (rubberBandRef.current) rubberBandRef.current.remove();
      rubberBandRef.current = L.polyline(
        [[points[points.length - 1].lat, points[points.length - 1].lng], [e.latlng.lat, e.latlng.lng]],
        { color: '#ef4444', weight: 2, dashArray: '5, 5', opacity: 0.6 }
      ).addTo(map);
    };

    drawDistanceLayers();

    map.on('click', onClick);
    if (!isMobileRef.current) {
      map.on('dblclick', onDblClick);
      map.on('mousemove', onMouseMove);
    }

    return () => {
      map.off('click', onClick);
      map.off('dblclick', onDblClick);
      map.off('mousemove', onMouseMove);
      map.doubleClickZoom.enable();
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      if (rubberBandRef.current) {
        rubberBandRef.current.remove();
        rubberBandRef.current = null;
      }
    };
  }, [activeTool, measurementData.distancePoints, map, setMeasurementData]);

  // ========================
  // AREA TOOL
  // ========================
  useEffect(() => {
    if (activeTool !== 'area') return;

    map.doubleClickZoom.disable();
    const points = measurementData.areaPoints;
    const isClosed = measurementData.areaValue > 0;

    const drawAreaLayers = () => {
      areaLayerGroup.current.clearLayers();
      if (points.length === 0) return;

      const latlngs = points.map(p => [p.lat, p.lng]);

      if (isClosed && points.length >= 3) {
        L.polygon(latlngs, {
          color: '#8b5cf6', weight: 2, fillColor: '#8b5cf6', fillOpacity: 0.15
        }).addTo(areaLayerGroup.current);
      } else if (points.length >= 2) {
        L.polyline(latlngs, {
          color: '#8b5cf6', weight: 2, dashArray: '8, 6'
        }).addTo(areaLayerGroup.current);
      }

      points.forEach(p => {
        L.circleMarker([p.lat, p.lng], {
          radius: 6, color: '#8b5cf6', fillColor: '#ffffff', fillOpacity: 1, weight: 2
        }).addTo(areaLayerGroup.current);
      });

      if (isClosed && points.length >= 3) {
        const areaSqFt = calculatePolygonAreaSqFt(points);
        const acres = (areaSqFt / 43560).toFixed(3);
        const centroid = getCentroid(points);

        L.tooltip({ permanent: true, direction: 'center', className: 'measurement-tooltip' })
          .setLatLng([centroid.lat, centroid.lng])
          .setContent(`${acres} ac`)
          .addTo(areaLayerGroup.current);
      }
    };

    const addAreaPoint = (latlng) => {
      const newPoint = { lat: latlng.lat, lng: latlng.lng };

      if (points.length >= 3) {
        const firstPoint = map.latLngToContainerPoint(L.latLng(points[0].lat, points[0].lng));
        const clickPoint = map.latLngToContainerPoint(latlng);
        if (firstPoint.distanceTo(clickPoint) < 15) {
          doClosePolygon(points);
          return;
        }
      }

      setMeasurementData(prev => ({
        ...prev,
        areaPoints: [...points, newPoint]
      }));
    };

    const onClick = (e) => {
      if (isClosed) return;
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => {
        addAreaPoint(e.latlng);
        clickTimerRef.current = null;
      }, 250);
    };

    const onDblClick = (e) => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      L.DomEvent.stopPropagation(e);
      L.DomEvent.preventDefault(e);
      if (points.length >= 3 && !isClosed) {
        doClosePolygon(points);
      }
    };

    const onMouseMove = (e) => {
      if (isMobileRef.current || points.length === 0 || isClosed) return;
      if (rubberBandRef.current) rubberBandRef.current.remove();
      rubberBandRef.current = L.polyline(
        [[points[points.length - 1].lat, points[points.length - 1].lng], [e.latlng.lat, e.latlng.lng]],
        { color: '#8b5cf6', weight: 2, dashArray: '5, 5', opacity: 0.6 }
      ).addTo(map);
    };

    drawAreaLayers();

    if (!isClosed) {
      map.on('click', onClick);
      if (!isMobileRef.current) {
        map.on('dblclick', onDblClick);
        map.on('mousemove', onMouseMove);
      }
    }

    return () => {
      map.off('click', onClick);
      map.off('dblclick', onDblClick);
      map.off('mousemove', onMouseMove);
      map.doubleClickZoom.enable();
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      if (rubberBandRef.current) {
        rubberBandRef.current.remove();
        rubberBandRef.current = null;
      }
    };
  }, [activeTool, measurementData.areaPoints, measurementData.areaValue, map, setMeasurementData]);

  const doClosePolygon = useCallback((points) => {
    const areaSqFt = calculatePolygonAreaSqFt(points);
    const perimeterMeters = calculatePerimeterMeters(
      points.map(p => L.latLng(p.lat, p.lng)), map
    );
    if (rubberBandRef.current) {
      rubberBandRef.current.remove();
      rubberBandRef.current = null;
    }
    setMeasurementData(prev => ({
      ...prev,
      areaValue: areaSqFt,
      areaPerimeter: perimeterMeters
    }));
  }, [map, setMeasurementData]);

  // ========================
  // COORDINATE DISPLAY
  // ========================
  useEffect(() => {
    if (activeTool !== 'coordinate') return;

    const handler = (e) => {
      const { lat, lng } = e.latlng;
      const zone = detectZone(lng);
      const sp = toStatePlane(lat, lng, zone);
      setMeasurementData(prev => ({
        ...prev,
        cursorCoords: { lat, lng, northing: sp.northing, easting: sp.easting, zone }
      }));
    };

    const eventType = isMobileRef.current ? 'click' : 'mousemove';
    map.on(eventType, handler);

    return () => {
      map.off(eventType, handler);
    };
  }, [activeTool, map, setMeasurementData]);

  return null;
}

function getCentroid(points) {
  let latSum = 0, lngSum = 0;
  points.forEach(p => { latSum += p.lat; lngSum += p.lng; });
  return { lat: latSum / points.length, lng: lngSum / points.length };
}

export default MeasurementController;
